import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";

// Gerçek bayrak görseli (flagcdn) — Windows'ta emoji bayrağı "TR" harfi olarak göründüğü için görsel kullanılır.
const Flag = ({ code }) => (
  <img
    src={`https://flagcdn.com/24x18/${code.toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/48x36/${code.toLowerCase()}.png 2x`}
    width={20} height={15} alt={code} loading="lazy" className="rounded-[2px] object-cover shrink-0"
  />
);

// Yaygın ülke kodları — TR varsayılan (ilk sırada). Bayrak sembolü + arama kodu.
export const COUNTRY_CODES = [
  { code: "TR", flag: "🇹🇷", dial: "+90", name: "Türkiye" },
  { code: "US", flag: "🇺🇸", dial: "+1", name: "ABD / Kanada" },
  { code: "GB", flag: "🇬🇧", dial: "+44", name: "İngiltere" },
  { code: "DE", flag: "🇩🇪", dial: "+49", name: "Almanya" },
  { code: "FR", flag: "🇫🇷", dial: "+33", name: "Fransa" },
  { code: "NL", flag: "🇳🇱", dial: "+31", name: "Hollanda" },
  { code: "BE", flag: "🇧🇪", dial: "+32", name: "Belçika" },
  { code: "AT", flag: "🇦🇹", dial: "+43", name: "Avusturya" },
  { code: "CH", flag: "🇨🇭", dial: "+41", name: "İsviçre" },
  { code: "IT", flag: "🇮🇹", dial: "+39", name: "İtalya" },
  { code: "ES", flag: "🇪🇸", dial: "+34", name: "İspanya" },
  { code: "SE", flag: "🇸🇪", dial: "+46", name: "İsveç" },
  { code: "RU", flag: "🇷🇺", dial: "+7", name: "Rusya" },
  { code: "AZ", flag: "🇦🇿", dial: "+994", name: "Azerbaycan" },
  { code: "SA", flag: "🇸🇦", dial: "+966", name: "S. Arabistan" },
  { code: "AE", flag: "🇦🇪", dial: "+971", name: "BAE" },
  { code: "QA", flag: "🇶🇦", dial: "+974", name: "Katar" },
  { code: "IQ", flag: "🇮🇶", dial: "+964", name: "Irak" },
  { code: "IR", flag: "🇮🇷", dial: "+98", name: "İran" },
  { code: "SY", flag: "🇸🇾", dial: "+963", name: "Suriye" },
  { code: "GR", flag: "🇬🇷", dial: "+30", name: "Yunanistan" },
  { code: "BG", flag: "🇧🇬", dial: "+359", name: "Bulgaristan" },
  { code: "RO", flag: "🇷🇴", dial: "+40", name: "Romanya" },
  { code: "UA", flag: "🇺🇦", dial: "+380", name: "Ukrayna" },
  { code: "CN", flag: "🇨🇳", dial: "+86", name: "Çin" },
  { code: "IN", flag: "🇮🇳", dial: "+91", name: "Hindistan" },
];

const DEFAULT = COUNTRY_CODES[0]; // TR

// Kayıtlı değeri (ör. "+90 5321234567") ülke + numara olarak ayrıştırır. Kod yoksa TR varsayılır.
export function splitPhone(value) {
  const v = (value || "").trim();
  if (v.startsWith("+")) {
    const sorted = [...COUNTRY_CODES].sort((a, b) => b.dial.length - a.dial.length);
    for (const c of sorted) {
      if (v.startsWith(c.dial)) return { country: c, number: v.slice(c.dial.length).trim() };
    }
  }
  return { country: DEFAULT, number: v };
}

// Bayraklı ülke kodu seçici + numara alanı. Değer birleşik string olarak yönetilir: "+90 5321234567".
// Ülke bazlı ulusal numara hane sayısı [min,max]; listede yoksa varsayılan aralık.
const PHONE_LEN = {
  TR: [10, 10], US: [10, 10], GB: [10, 10], DE: [10, 11], FR: [9, 9], NL: [9, 9],
  BE: [8, 9], AT: [10, 11], CH: [9, 9], IT: [9, 10], ES: [9, 9], SE: [7, 9],
  RU: [10, 10], AZ: [9, 9], SA: [9, 9], AE: [9, 9], QA: [8, 8], IQ: [10, 10],
  IR: [10, 10], SY: [9, 9], GR: [10, 10], BG: [8, 9], RO: [9, 9], UA: [9, 9],
  CN: [11, 11], IN: [10, 10],
};
const DEFAULT_LEN = [7, 14];

export function phoneDigits(number) { return (number || "").replace(/\D/g, ""); }

// Numara geçerli mi? Boş = geçerli (zorunluluk formda ayrı kontrol edilir). Doluysa ülkeye göre hane kontrolü.
export function isValidPhone(value) {
  const v = (value || "").trim();
  if (!v) return true;
  const { country, number } = splitPhone(v);
  const digits = phoneDigits(number);
  const [mn, mx] = PHONE_LEN[country.code] || DEFAULT_LEN;
  return digits.length >= mn && digits.length <= mx;
}

export function PhoneInput({ value, onChange, testid = "phone", placeholder = "5xx xxx xx xx", className = "", required = false }) {
  const [touched, setTouched] = useState(false);
  const { country, number } = splitPhone(value);
  const digits = phoneDigits(number);
  const [mn, mx] = PHONE_LEN[country.code] || DEFAULT_LEN;
  const emptyReq = required && digits.length === 0;
  const badLen = digits.length > 0 && (digits.length < mn || digits.length > mx);
  const invalid = touched && (emptyReq || badLen);
  const lenLabel = mn === mx ? `${mn} haneli` : `${mn}-${mx} haneli`;
  const emit = (dial, num) => {
    const cleaned = (num || "").trim();
    onChange(cleaned ? `${dial} ${cleaned}` : "");
  };
  return (
    <div className={className}>
      <div className="flex gap-2">
        <Select value={country.code} onValueChange={(code) => { const c = COUNTRY_CODES.find((x) => x.code === code) || DEFAULT; emit(c.dial, number); }}>
          <SelectTrigger data-testid={`${testid}-country`} className="h-10 w-[112px] shrink-0 px-2.5" title={country.name}>
            <div className="flex items-center gap-1.5"><Flag code={country.code} /><span className="tabular text-sm leading-none">{country.dial}</span></div>
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {COUNTRY_CODES.map((c) => (
              <SelectItem key={c.code} value={c.code} textValue={`${c.dial} ${c.name}`} data-testid={`${testid}-country-${c.code}`}>
                <span className="flex items-center gap-2"><Flag code={c.code} /><span className="tabular w-12">{c.dial}</span><span className="text-xs text-slate-400">{c.name}</span></span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid={testid}
          value={number}
          onChange={(e) => emit(country.dial, e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          inputMode="tel"
          aria-invalid={invalid}
          className={`flex-1 ${invalid ? "border-red-400 focus-visible:ring-red-400" : ""}`}
        />
      </div>
      {invalid && (
        <p data-testid={`${testid}-error`} className="mt-1 text-[11px] font-medium text-red-500">
          {emptyReq ? "Telefon numarası zorunlu" : `Geçersiz numara — ${country.name} için ${lenLabel} olmalı (girilen: ${digits.length}).`}
        </p>
      )}
    </div>
  );
}
